import { defineStore } from 'pinia';
import GeoMoverDB from '@/db/geoMoverDB';
import { appDebugLog } from '@/utils/appDebugLog';

function normalizeOptionalString (value) {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function normalizeKeywords (value) {
  if (!Array.isArray(value)) return [];
  const keywords = value
    .map((entry) => normalizeOptionalString(entry)?.toLowerCase())
    .filter(Boolean);
  return [...new Set(keywords)];
}

function normalizeSortOrder (value, fallback = 0) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return fallback;
  return Math.max(0, Math.round(numeric));
}

function normalizeEntry (input = {}, fallbackSortOrder = 0) {
  return {
    ...input,
    label: String(input?.label || '').trim(),
    iconKey: String(input?.iconKey || '').trim(),
    library: normalizeOptionalString(input?.library),
    category: normalizeOptionalString(input?.category),
    keywords: normalizeKeywords(input?.keywords),
    isEnabled: Boolean(input?.isEnabled),
    sortOrder: normalizeSortOrder(input?.sortOrder, fallbackSortOrder)
  };
}

function sortEntries (rows) {
  return [...rows].sort((first, second) => {
    const firstOrder = normalizeSortOrder(first?.sortOrder, 0);
    const secondOrder = normalizeSortOrder(second?.sortOrder, 0);
    if (firstOrder !== secondOrder) return firstOrder - secondOrder;

    const firstLabel = String(first?.label || '').toLowerCase();
    const secondLabel = String(second?.label || '').toLowerCase();
    if (firstLabel !== secondLabel) return firstLabel.localeCompare(secondLabel);

    return Number(first?.id || 0) - Number(second?.id || 0);
  });
}

export const useIconCatalogStore = defineStore('iconCatalog', {
  state: () => ({
    entries: [],
    isLoaded: false
  }),

  getters: {
    getAllIconEntries: (state) => state.entries,
    getEnabledIconEntries: (state) => state.entries.filter((entry) => Boolean(entry?.isEnabled)),
    getIconEntryByKey: (state) => (iconKey) => {
      const normalizedKey = String(iconKey || '').trim();
      if (!normalizedKey) return null;
      return state.entries.find((entry) => String(entry?.iconKey || '').trim() === normalizedKey) || null;
    }
  },

  actions: {
    async loadIconCatalog () {
      try {
        const rows = await GeoMoverDB.findAll('iconCatalog');
        this.entries = sortEntries(
          (Array.isArray(rows) ? rows : [])
            .map((row, index) => normalizeEntry(row, (index + 1) * 10))
            .filter((row) => row.label && row.iconKey)
        );
        this.isLoaded = true;
        return this.entries;
      }
      catch (error) {
        appDebugLog.error('IconCatalogStore', 'Error loading icon catalog:', error);
        this.entries = [];
        this.isLoaded = false;
        return [];
      }
    },

    async setIconEnabled (entryId, isEnabled) {
      const numericId = Number(entryId);
      if (!Number.isFinite(numericId)) {
        throw new Error('Valid icon entry ID is required.');
      }

      if (!this.isLoaded) {
        await this.loadIconCatalog();
      }

      const current = this.entries.find((entry) => Number(entry?.id) === numericId)
        || await GeoMoverDB.findOne('iconCatalog', numericId);
      if (!current) {
        throw new Error('Icon catalog entry not found.');
      }

      const next = {
        ...current,
        isEnabled: Boolean(isEnabled),
        updatedAt: Date.now()
      };

      await GeoMoverDB.updateOne('iconCatalog', next, numericId);
      this.entries = sortEntries([
        ...this.entries.filter((entry) => Number(entry?.id) !== numericId),
        next
      ]);
      this.isLoaded = true;
      return next;
    },

    async createIconCatalogEntry (input = {}) {
      if (!this.isLoaded) {
        await this.loadIconCatalog();
      }

      const normalized = normalizeEntry(input, (this.entries.length + 1) * 10);
      if (!normalized.label) {
        throw new Error('Icon label is required.');
      }
      if (!normalized.iconKey) {
        throw new Error('Icon key is required.');
      }

      const duplicate = this.entries.find((entry) => String(entry?.iconKey || '').trim() === normalized.iconKey);
      if (duplicate) {
        throw new Error('This icon already exists in the shared catalog.');
      }

      const now = Date.now();
      const payload = {
        ...normalized,
        createdAt: now,
        updatedAt: null
      };

      const id = await GeoMoverDB.addOne('iconCatalog', payload);
      const persisted = { ...payload, id };
      this.entries = sortEntries([...this.entries, persisted]);
      this.isLoaded = true;
      return persisted;
    },

    async deleteIconCatalogEntry (entryId) {
      const numericId = Number(entryId);
      if (!Number.isFinite(numericId)) {
        throw new Error('Valid icon entry ID is required.');
      }

      if (!this.isLoaded) {
        await this.loadIconCatalog();
      }

      const current = this.entries.find((entry) => Number(entry?.id) === numericId)
        || await GeoMoverDB.findOne('iconCatalog', numericId);
      if (!current) {
        throw new Error('Icon catalog entry not found.');
      }

      await GeoMoverDB.deleteOne('iconCatalog', numericId);
      this.entries = sortEntries(this.entries.filter((entry) => Number(entry?.id) !== numericId));
      this.isLoaded = true;
      return current;
    }
  }
});
